import { useState } from "react";
import { Navigate } from "react-router-dom";
import { CursorTable, type CursorTableColumn } from "@core/table";
import { useSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import type { components } from "@core/types/openapi";
import {
  AdvancedFiltersModal,
  copyToClipboard,
  EmptyState,
  PortalButton,
  securityLogFilterBarStyles,
  SecurityHeader,
  SecurityLogFilterBar,
  RowIconAction,
  SidePanel,
  SidePanelHeader,
  Toggle,
} from "@portal/common";
import type { HttpRequestLogFilters } from "./api";
import {
  useHttpRequestLog,
  useHttpRequestLogDetail,
} from "./httpRequestLogQueries";
import { httpRequestLogStyles } from "./httpRequestLogStyles";

type HttpRequestLogSummary = components["schemas"]["HttpRequestLogSummary"];
type Surface = HttpRequestLogSummary["surface"];
type SurfaceFilter = "all" | Surface;
type ExceptionFilter = "all" | "with" | "without";

const surfaceLabel: Record<Surface, string> = {
  portal: "Portal administrativo",
  app: "Aplicación móvil",
  validator: "Validador QR",
  other: "Otra",
};

const dateTimeFormatter = new Intl.DateTimeFormat("es-EC", {
  timeZone: "America/Guayaquil",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function formatInstant(value: string): string {
  const parts = dateTimeFormatter.formatToParts(new Date(value));
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${getPart("day")} ${getPart("month")} ${getPart("year")} · ${getPart("hour")}:${getPart("minute")}:${getPart("second")}`;
}

function pretty(value: unknown): string {
  if (value === null || value === undefined) return "null";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function JsonBlock({ value }: { value: unknown }) {
  return <pre style={httpRequestLogStyles.detail.json}>{pretty(value)}</pre>;
}

function CopyTraceIdButton({ correlationId }: { correlationId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <PortalButton
      type="button"
      tone="secondary"
      size="small"
      onClick={() => copyToClipboard(correlationId, setCopied)}
    >
      {copied ? "Copiado" : "Copiar trace id"}
    </PortalButton>
  );
}

// "Query"/"Headers"/"Body" del request y "Headers"/"Body" de la response
// repiten el mismo bloque (rótulo + `JsonBlock`), tanto entre sí como contra
// el patrón ya usado por otras pantallas (jscpd, revisión de duplicación
// 2026-09-23). El último bloque de cada sección no lleva margen inferior.
function LabeledJsonBlock({
  label,
  value,
  last,
}: {
  label: string;
  value: unknown;
  last?: boolean;
}) {
  return (
    <div style={last ? undefined : { marginBottom: 10 }}>
      <div className="label" style={httpRequestLogStyles.detail.fieldLabel}>
        {label}
      </div>
      <JsonBlock value={value} />
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label" style={httpRequestLogStyles.detail.fieldLabel}>
        {label}
      </div>
      <div style={httpRequestLogStyles.detail.fieldValue}>{value}</div>
    </div>
  );
}

function HttpRequestLogDetailPanel({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const detail = useHttpRequestLogDetail(id);
  const data = detail.data;
  return (
    <SidePanel label="Detalle de la petición" width={640} onClose={onClose}>
      <SidePanelHeader
        label="Detalle de la petición"
        title={data ? `${data.method} ${data.path}` : "—"}
      >
        {data ? (
          <div style={{ marginTop: 10 }}>
            <CopyTraceIdButton correlationId={data.correlationId} />
          </div>
        ) : null}
      </SidePanelHeader>
      <div style={{ flex: 1, overflowY: "auto", padding: "22px 26px 30px" }}>
        {detail.isLoading ? <div>Cargando…</div> : null}
        {detail.isError ? <EmptyState kind="error" /> : null}
        {data ? (
          <>
            <div style={httpRequestLogStyles.detail.section}>
              <div
                className="label"
                style={httpRequestLogStyles.detail.sectionTitle}
              >
                Resumen
              </div>
              <div style={httpRequestLogStyles.detail.sectionBody}>
                <div style={httpRequestLogStyles.detail.summaryGrid}>
                  <DetailField label="Trace id" value={data.correlationId} />
                  <DetailField
                    label="Fecha y hora"
                    value={formatInstant(data.occurredAt)}
                  />
                  <DetailField
                    label="Duración"
                    value={`${data.durationMs} ms`}
                  />
                  <DetailField label="Estado" value={String(data.status)} />
                  <DetailField
                    label="Superficie"
                    value={surfaceLabel[data.surface]}
                  />
                  <DetailField
                    label="Ruta"
                    value={data.route ?? "(sin resolver)"}
                  />
                  <DetailField
                    label="Actor"
                    value={
                      data.actorType
                        ? `${data.actorType} · ${data.actorId ?? "—"}`
                        : "Sin actor"
                    }
                  />
                  <DetailField
                    label="IP"
                    value={`${data.ip ?? "—"}${data.userAgent ? ` · ${data.userAgent}` : ""}`}
                  />
                </div>
              </div>
            </div>

            <div style={httpRequestLogStyles.detail.section}>
              <div
                className="label"
                style={httpRequestLogStyles.detail.sectionTitle}
              >
                Request
              </div>
              <div style={httpRequestLogStyles.detail.sectionBody}>
                <LabeledJsonBlock label="Query" value={data.query} />
                <LabeledJsonBlock label="Headers" value={data.requestHeaders} />
                <LabeledJsonBlock
                  label={`Body${data.bodyTruncated ? " (recortado)" : ""}`}
                  value={data.requestBody}
                  last
                />
              </div>
            </div>

            <div style={httpRequestLogStyles.detail.section}>
              <div
                className="label"
                style={httpRequestLogStyles.detail.sectionTitle}
              >
                Response
              </div>
              <div style={httpRequestLogStyles.detail.sectionBody}>
                <LabeledJsonBlock
                  label="Headers"
                  value={data.responseHeaders}
                />
                <LabeledJsonBlock
                  label={`Body${data.bodyTruncated ? " (recortado)" : ""}`}
                  value={data.responseBody}
                  last
                />
              </div>
            </div>

            {data.hasException ? (
              <div style={httpRequestLogStyles.detail.section}>
                <div
                  className="label"
                  style={{
                    ...httpRequestLogStyles.detail.sectionTitle,
                    color: tokens.danger.base,
                  }}
                >
                  Excepción
                </div>
                <div style={httpRequestLogStyles.detail.sectionBody}>
                  <JsonBlock value={data.error} />
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </SidePanel>
  );
}

export function HttpRequestLogScreen() {
  const { session } = useSession();
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [correlationId, setCorrelationId] = useState("");
  const [method, setMethod] = useState("");
  const [pathContains, setPathContains] = useState("");
  const [surface, setSurface] = useState<SurfaceFilter>("all");
  const [status, setStatus] = useState("");
  const [actorId, setActorId] = useState("");
  const [hasException, setHasException] = useState<ExceptionFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const filters: HttpRequestLogFilters = {
    ...(correlationId.trim() ? { correlationId: correlationId.trim() } : {}),
    ...(method.trim() ? { method: method.trim().toUpperCase() } : {}),
    ...(pathContains.trim() ? { pathContains: pathContains.trim() } : {}),
    ...(surface === "all" ? {} : { surface }),
    ...(status.trim() ? { status: Number.parseInt(status.trim(), 10) } : {}),
    ...(onlyErrors ? { onlyErrors: true } : {}),
    ...(hasException === "all"
      ? {}
      : { hasException: hasException === "with" }),
    ...(actorId.trim() ? { actorId: actorId.trim() } : {}),
    ...(from ? { from: new Date(from).toISOString() } : {}),
    ...(to ? { to: new Date(to).toISOString() } : {}),
  };
  const httpRequestLog = useHttpRequestLog(filters);
  const rows = httpRequestLog.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = httpRequestLog.data?.pages.at(-1);
  const hasAdvancedFilters =
    correlationId ||
    method ||
    pathContains ||
    surface !== "all" ||
    status ||
    actorId ||
    hasException !== "all" ||
    from ||
    to;

  // "Ocultar no es control de acceso" (ADR-011): el servidor rechaza igual a
  // quien no sea Administrador. Este redirect solo evita dejar a alguien
  // parado en una pantalla vacía/en error si llega por URL directa. Va
  // después de todos los hooks de arriba para no romper su orden entre
  // renders (session pasa de `undefined` a la sesión real).
  if (
    session !== undefined &&
    session !== null &&
    !session.user.isAdministrator
  ) {
    return <Navigate to="/security/access-log" replace />;
  }

  const columns: CursorTableColumn<HttpRequestLogSummary>[] = [
    {
      key: "occurredAt",
      header: "Fecha y hora",
      width: "190px",
      render: (entry) => (
        <span style={{ color: tokens.text.tertiary }}>
          {formatInstant(entry.occurredAt)}
        </span>
      ),
    },
    {
      key: "method",
      header: "Método",
      width: "90px",
      render: (entry) => (
        <span style={httpRequestLogStyles.methodText}>{entry.method}</span>
      ),
    },
    {
      key: "path",
      header: "Ruta",
      width: "1.6fr",
      render: (entry) => (
        <span style={httpRequestLogStyles.pathText}>{entry.path}</span>
      ),
    },
    {
      key: "status",
      header: "Estado",
      width: "110px",
      render: (entry) => (
        <span
          style={
            entry.status >= 400
              ? httpRequestLogStyles.errorBadge
              : httpRequestLogStyles.successBadge
          }
        >
          {entry.status}
        </span>
      ),
    },
    {
      key: "surface",
      header: "Superficie",
      width: "1fr",
      render: (entry) => (
        <span style={{ color: tokens.text.tertiary }}>
          {surfaceLabel[entry.surface]}
        </span>
      ),
    },
    {
      key: "actor",
      header: "Actor",
      width: "1fr",
      render: (entry) => (
        <span style={{ color: tokens.text.tertiary }}>
          {entry.actorType
            ? `${entry.actorType} · ${entry.actorId ?? "—"}`
            : "—"}
        </span>
      ),
    },
    {
      key: "exception",
      header: "Excepción",
      width: "90px",
      render: (entry) =>
        entry.hasException ? (
          <span style={httpRequestLogStyles.exceptionMark}>⚠</span>
        ) : (
          <span style={{ color: tokens.text.disabled }}>—</span>
        ),
    },
  ];

  return (
    <div>
      <SecurityHeader />
      <SecurityLogFilterBar
        ariaLabel="Filtros de la traza HTTP"
        groupLabel="Estado"
        chips={
          [
            [false, "Todos"],
            [true, "Solo errores"],
          ] as const
        }
        activeValue={onlyErrors}
        onChipChange={setOnlyErrors}
        trailingText={`Filtros adicionales · ${lastPage?.meta.total ?? 0} registros`}
        trailingAriaLabel="Abrir filtros adicionales"
        onOpenAdvanced={() => setAdvancedFiltersOpen(true)}
      />

      {advancedFiltersOpen ? (
        <AdvancedFiltersModal
          eyebrow="Seguridad · Traza HTTP"
          label="Filtros adicionales de la traza HTTP"
          width={620}
          onClose={() => setAdvancedFiltersOpen(false)}
          description="Consultá por trace id, método, ruta, superficie, estado, actor y excepción."
        >
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Trace id (correlación)
            <input
              aria-label="Filtrar por trace id"
              placeholder="UUID de correlación"
              value={correlationId}
              onChange={(event) => setCorrelationId(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Método
            <input
              aria-label="Filtrar por método HTTP"
              placeholder="GET, POST…"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Ruta contiene
            <input
              aria-label="Filtrar por subcadena de la ruta"
              placeholder="/redemptions"
              value={pathContains}
              onChange={(event) => setPathContains(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Superficie
            <select
              aria-label="Filtrar por superficie"
              value={surface}
              onChange={(event) =>
                setSurface(event.target.value as SurfaceFilter)
              }
              style={securityLogFilterBarStyles.filterInput}
            >
              <option value="all">Superficie · todas</option>
              <option value="portal">Portal administrativo</option>
              <option value="app">Aplicación móvil</option>
              <option value="validator">Validador QR</option>
              <option value="other">Otra</option>
            </select>
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Estado (código exacto)
            <input
              aria-label="Filtrar por código de estado exacto"
              placeholder="500"
              inputMode="numeric"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Actor (id)
            <input
              aria-label="Filtrar por identificador de actor"
              placeholder="ID de staff o miembro"
              value={actorId}
              onChange={(event) => setActorId(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Desde
            <input
              aria-label="Fecha y hora desde"
              type="datetime-local"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Hasta
            <input
              aria-label="Fecha y hora hasta"
              type="datetime-local"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <div style={httpRequestLogStyles.advancedFilters.toggleRow}>
            <Toggle
              on={hasException !== "all"}
              onChange={(next) => setHasException(next ? "with" : "all")}
              ariaLabel="Con excepción"
            />
            Con excepción
          </div>
        </AdvancedFiltersModal>
      ) : null}

      {httpRequestLog.isLoading ? (
        <div style={{ padding: 22 }}>Cargando registros…</div>
      ) : httpRequestLog.isError ? (
        <EmptyState kind="error" />
      ) : rows.length ? (
        <CursorTable
          columns={columns}
          rows={rows}
          rowKey={(entry) => entry.id}
          page={{
            nextCursor: lastPage?.meta.next ?? null,
            totalCount: lastPage?.meta.total ?? rows.length,
          }}
          onLoadMore={() => httpRequestLog.fetchNextPage()}
          isLoadingMore={httpRequestLog.isFetchingNextPage}
          minWidth={httpRequestLogStyles.table.minWidth}
          headerPadding={httpRequestLogStyles.table.headerPadding}
          rowPadding={httpRequestLogStyles.table.rowPadding}
          rowFont={`400 11.5px ${tokens.font.ui}`}
          showFooter={Boolean(lastPage?.meta.next)}
          rowActions={(entry) => (
            <RowIconAction
              label="Ver detalle"
              icon="eye"
              onClick={() => setOpenId(entry.id)}
            />
          )}
        />
      ) : (
        <EmptyState
          kind={hasAdvancedFilters || onlyErrors ? "no-results" : "no-data"}
        />
      )}

      {openId ? (
        <HttpRequestLogDetailPanel
          id={openId}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}
